// A supplier's statement (docs/plans/supplier-statement.md): every bill, payment and return
// with a running balance of what the shop owes, worked out on the device from the payables
// it already shows. Nothing here is stored. Cancelled bills, payments and returns are left
// out. Pure functions, unit-tested; the customer's statement (statement.ts) is the model.
import { adToBs, daysOverdue, formatBsDate } from '@ims/core'
import type {
  PaymentMethod,
  SupplierBillSummary,
  SupplierCreditSummary,
  SupplierPaymentSummary,
} from '@ims/data'
import type { DateRange } from './reports.ts'
import { methodLabel, plainRupees } from './statement.ts'

export type SupplierRowKind = 'bill' | 'return' | 'payment'

export interface SupplierStatementRow {
  kind: SupplierRowKind
  id: string
  date: string
  /** The supplier's bill number, or a return's credit number. */
  number: string | null
  dueDate: string | null
  /** A bill with no delivery behind it (an old balance, a charge). */
  noDelivery: boolean
  method: PaymentMethod | null
  reference: string | null
  /** A payment's or a return's bills, by number. */
  bills: string[]
  /** A return's reason. */
  reason: string | null
  billedPaisa: number
  /** A payment or a return. */
  paidPaisa: number
  /** What the shop owes after this line. */
  balancePaisa: number
  /** The part of a payment or return set against no bill: an advance with the supplier. */
  aheadPaisa: number
}

export interface SupplierStatement extends DateRange {
  openingPaisa: number
  rows: SupplierStatementRow[]
  billedPaisa: number
  paidPaisa: number
  closingPaisa: number
  /** Money paid in the period, by method, largest first; returns on their own. */
  byMethod: { method: PaymentMethod; paisa: number }[]
  returnedPaisa: number
}

export interface SupplierLedger {
  bills: SupplierBillSummary[]
  payments: SupplierPaymentSummary[]
  credits: SupplierCreditSummary[]
}

const ORDER: Record<SupplierRowKind, number> = { bill: 0, return: 1, payment: 2 }
const day = (iso: string) => iso.slice(0, 10)

function lines(ledger: SupplierLedger): (SupplierStatementRow & { createdAt: string })[] {
  const out: (SupplierStatementRow & { createdAt: string })[] = []
  const base = {
    number: null,
    dueDate: null,
    noDelivery: false,
    method: null,
    reference: null,
    bills: [],
    reason: null,
    billedPaisa: 0,
    paidPaisa: 0,
    balancePaisa: 0,
    aheadPaisa: 0,
  }
  for (const { bill } of ledger.bills) {
    if (bill.status !== 'active') continue
    out.push({
      ...base,
      kind: 'bill',
      id: bill.id,
      date: day(bill.bill_date),
      createdAt: bill.created_at,
      number: bill.bill_no || null,
      dueDate: bill.due_date ? day(bill.due_date) : null,
      noDelivery: !bill.goods_receipt_id,
      reason: bill.goods_receipt_id ? null : bill.notes,
      billedPaisa: bill.total_paisa,
    })
  }
  for (const c of ledger.credits) {
    if (c.credit.status !== 'active') continue
    out.push({
      ...base,
      kind: 'return',
      id: c.credit.id,
      date: day(c.credit.credit_date),
      createdAt: c.credit.created_at,
      number: c.credit.credit_no || null,
      bills: c.bills,
      reason: c.credit.reason,
      paidPaisa: c.credit.total_paisa,
      aheadPaisa: Math.max(0, c.credit.total_paisa - c.allocatedPaisa),
    })
  }
  for (const p of ledger.payments) {
    if (p.payment.status !== 'active') continue
    out.push({
      ...base,
      kind: 'payment',
      id: p.payment.id,
      date: day(p.payment.payment_date),
      createdAt: p.payment.created_at,
      method: p.payment.method,
      reference: p.payment.reference,
      bills: p.bills,
      paidPaisa: p.payment.amount_paisa,
      aheadPaisa: Math.max(0, p.payment.amount_paisa - p.allocatedPaisa),
    })
  }
  return out.sort(
    (a, b) =>
      a.date.localeCompare(b.date) ||
      ORDER[a.kind] - ORDER[b.kind] ||
      a.createdAt.localeCompare(b.createdAt),
  )
}

/** The statement for `range`: owed before it, every line in it, owed after it. */
export function buildSupplierStatement(
  ledger: SupplierLedger,
  range: DateRange,
): SupplierStatement {
  let balance = 0
  let opening = 0
  let billed = 0
  let paid = 0
  let returned = 0
  const methods = new Map<PaymentMethod, number>()
  const rows: SupplierStatementRow[] = []
  for (const { createdAt: _createdAt, ...line } of lines(ledger)) {
    if (line.date > range.to) break
    balance += line.billedPaisa - line.paidPaisa
    if (line.date < range.from) {
      opening = balance
      continue
    }
    billed += line.billedPaisa
    paid += line.paidPaisa
    if (line.kind === 'return') returned += line.paidPaisa
    if (line.kind === 'payment' && line.method)
      methods.set(line.method, (methods.get(line.method) ?? 0) + line.paidPaisa)
    rows.push({ ...line, balancePaisa: balance })
  }
  return {
    ...range,
    openingPaisa: opening,
    rows,
    billedPaisa: billed,
    paidPaisa: paid,
    closingPaisa: opening + billed - paid,
    byMethod: [...methods]
      .map(([method, paisa]) => ({ method, paisa }))
      .sort((a, b) => b.paisa - a.paisa),
    returnedPaisa: returned,
  }
}

export interface SupplierOpenBill {
  id: string
  number: string | null
  date: string
  dueDate: string | null
  totalPaisa: number
  /** Paid and returned against it. */
  settledPaisa: number
  openPaisa: number
  /** Days past its due date; 0 when not late or it has no due date. */
  lateDays: number
}

export const LATE_AGES = [
  'Not due yet',
  '1–30 days late',
  '31–60 days late',
  '61–90 days late',
  'Over 90 days',
] as const

export interface SupplierStanding {
  /** Every bill, less every payment and return. */
  owedPaisa: number
  /** Paid or returned beyond the bills: it comes off their next bill. */
  advancePaisa: number
  openBills: SupplierOpenBill[]
  /** Open money by how late its bill is (`LATE_AGES`). */
  ageing: [number, number, number, number, number]
}

/** What is owed now, the open bills and how late they are, as of `today`. */
export function supplierStanding(ledger: SupplierLedger, today: string): SupplierStanding {
  const openBills: SupplierOpenBill[] = []
  for (const { bill, coveredPaisa } of ledger.bills) {
    if (bill.status !== 'active') continue
    const open = bill.total_paisa - coveredPaisa
    if (open <= 0) continue
    openBills.push({
      id: bill.id,
      number: bill.bill_no || null,
      date: day(bill.bill_date),
      dueDate: bill.due_date ? day(bill.due_date) : null,
      totalPaisa: bill.total_paisa,
      settledPaisa: coveredPaisa,
      openPaisa: open,
      lateDays: bill.due_date ? Math.max(0, daysOverdue(day(bill.due_date), today)) : 0,
    })
  }
  openBills.sort((a, b) => a.date.localeCompare(b.date))
  const ageing: [number, number, number, number, number] = [0, 0, 0, 0, 0]
  for (const b of openBills) {
    const d = b.lateDays
    ageing[d <= 0 ? 0 : d <= 30 ? 1 : d <= 60 ? 2 : d <= 90 ? 3 : 4] += b.openPaisa
  }
  const sum = (xs: number[]) => xs.reduce((a, x) => a + x, 0)
  const activeBills = ledger.bills.filter((b) => b.bill.status === 'active')
  const activePays = ledger.payments.filter((p) => p.payment.status === 'active')
  const activeCredits = ledger.credits.filter((c) => c.credit.status === 'active')
  return {
    owedPaisa:
      sum(activeBills.map((b) => b.bill.total_paisa)) -
      sum(activePays.map((p) => p.payment.amount_paisa)) -
      sum(activeCredits.map((c) => c.credit.total_paisa)),
    advancePaisa:
      sum(activePays.map((p) => Math.max(0, p.payment.amount_paisa - p.allocatedPaisa))) +
      sum(activeCredits.map((c) => Math.max(0, c.credit.total_paisa - c.allocatedPaisa))),
    openBills,
    ageing,
  }
}

/** "Bill", "Return", "Payment · cash". */
export function supplierRowWhat(row: Pick<SupplierStatementRow, 'kind' | 'method'>): string {
  if (row.kind === 'bill') return 'Bill'
  if (row.kind === 'return') return 'Return'
  return row.method ? `Payment · ${methodLabel(row.method).toLowerCase()}` : 'Payment'
}

/** The words after "Bill NT-2201" on a row: its due date, or what a payment paid. */
export function supplierRowDetail(
  row: SupplierStatementRow,
  dayLabel: (iso: string) => string,
): string {
  if (row.kind === 'bill')
    return [
      row.noDelivery && (row.reason || 'no delivery'),
      row.dueDate && `due ${dayLabel(row.dueDate)}`,
    ]
      .filter(Boolean)
      .join(' · ')
  const parts = [
    row.kind === 'return' && row.reason,
    row.reference && (row.kind === 'payment' ? `ref ${row.reference}` : row.reference),
    row.bills.length > 0 && `for ${row.bills.join(', ')}`,
    row.aheadPaisa > 0 && 'advance',
  ]
  return parts.filter(Boolean).join(' · ')
}

function csvField(value: string): string {
  return /[",\r\n]/.test(value) ? `"${value.replace(/"/g, '""')}"` : value
}

/** The statement as CSV, for an accountant: BOM, CRLF, AD and BS dates, plain numbers. */
export function supplierStatementCsv(statement: SupplierStatement): string {
  const bs = (iso: string) => {
    try {
      return formatBsDate(adToBs(iso), 'iso')
    } catch {
      return ''
    }
  }
  const head = [
    'Date (AD)',
    'Date (BS)',
    'Type',
    'Their bill no.',
    'Due',
    'Method',
    'Reference',
    'Paid for',
    'Billed',
    'Paid or returned',
    'Balance',
  ]
  const rows: string[][] = [
    head,
    [
      statement.from,
      bs(statement.from),
      'Balance before this period',
      '',
      '',
      '',
      '',
      '',
      '',
      '',
      plainRupees(statement.openingPaisa),
    ],
  ]
  for (const r of statement.rows) {
    rows.push([
      r.date,
      bs(r.date),
      r.kind === 'bill' ? 'Bill' : r.kind === 'return' ? 'Return' : 'Payment',
      r.kind === 'bill' ? (r.number ?? '') : r.kind === 'return' ? (r.number ?? '') : '',
      r.dueDate ?? '',
      r.method ? methodLabel(r.method) : '',
      r.kind === 'return' ? (r.reason ?? '') : (r.reference ?? ''),
      r.bills.join('; '),
      r.billedPaisa ? plainRupees(r.billedPaisa) : '',
      r.paidPaisa ? plainRupees(r.paidPaisa) : '',
      plainRupees(r.balancePaisa),
    ])
  }
  return '﻿' + rows.map((r) => r.map(csvField).join(',')).join('\r\n') + '\r\n'
}
